import { type Dispatch, type SetStateAction, useMemo } from "react";

import { useTranslation } from "react-i18next";

import { MailIcon } from "lucide-react";

import type { IdeaWithCoverImage, OpenAPI } from "simplydo/interfaces";
import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback";
import { Button } from "simplydo/react/ui/button";
import { H4, H5 } from "simplydo/react/ui/header";
import { Message } from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { Table, TableBody, TableCell, TableRow } from "simplydo/react/ui/table";

import util from "utils/utils";

import { UserChip } from "components/lib/Chips";
import BaseChip from "components/lib/Chips/BaseChip";
import type { ChipUser } from "components/lib/Chips/types";
import {
  AssessmentRoundLabel,
  RoundLabelsGroup,
} from "components/lib/UI/AssessmentRoundLabel";

import type { LocalAssignments } from "./AssignmentDashboard";

type AssessmentRound = NonNullable<
  NonNullable<OpenAPI.Schemas["IdeaTemplate"]["assessments"]>["rounds"]
>[number];

type InspectedIdea = OpenAPI.Schemas["Idea"] & { emailInvites?: any[] };

type IdeaInspectModalProps = {
  inspectedIdea: InspectedIdea | null;
  setInspectedIdea: Dispatch<SetStateAction<OpenAPI.Schemas["Idea"] | null>>;
  localAssignments: LocalAssignments | null;
  setLocalUserAssignments: Dispatch<SetStateAction<LocalAssignments>>;
  setLocalEmailAssignments: Dispatch<SetStateAction<LocalAssignments>>;
  unassignAssessor: (
    ideaId: string,
    assessorId: string,
    assessmentRoundId: string,
  ) => void;
  assessmentRoundMap: Record<string, AssessmentRound>;
};

export const IdeaInspectModal = ({
  inspectedIdea,
  setInspectedIdea,
  localAssignments,
  setLocalUserAssignments,
  setLocalEmailAssignments,
  unassignAssessor,
  assessmentRoundMap,
}: IdeaInspectModalProps) => {
  const { t } = useTranslation();
  const ideaId = inspectedIdea?._id ?? "";

  const assessmentsByUser = useMemo(() => {
    if (!inspectedIdea?.assessments) {
      return {};
    }
    const assessmentsMap: Record<
      string,
      NonNullable<OpenAPI.Schemas["Idea"]["assessments"]>[number][]
    > = {};
    for (const assessment of inspectedIdea.assessments) {
      if (!assessmentsMap[assessment.user]) {
        assessmentsMap[assessment.user] = [];
      }
      assessmentsMap[assessment.user]!.push(assessment);
    }
    return assessmentsMap;
  }, [inspectedIdea?.assessments]);

  return (
    <Modal open={!!inspectedIdea} onClose={() => setInspectedIdea(null)}>
      {inspectedIdea ? (
        <>
          <ModalHeader>
            {t("common:capitalise", { key: "generic.idea" })} assessments
          </ModalHeader>

          <ModalContent scrolling>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                overflow: "hidden",
                gap: 6,
                paddingRight: 12,
              }}
            >
              <ImageWithFallback
                avatar
                style={{
                  minWidth: 45,
                  width: 45,
                  height: 35,
                  borderRadius: 4,
                  objectFit: "cover",
                }}
                src={util.ideaCoverImage(
                  inspectedIdea as IdeaWithCoverImage,
                  "200x200",
                )}
                fallbackSrc={util.ideaCoverImage(undefined)}
              />
              <div>
                <div style={{ fontSize: 14, fontWeight: "bold" }}>
                  {inspectedIdea.name}
                </div>
              </div>
            </div>

            <H4>Draft assessments</H4>
            {!util.isEmpty(localAssignments) ? (
              <>
                <Message color="blue">
                  These assignments are currently only in draft. Before the
                  users is notified the assignments must first be finalised and
                  saved.
                </Message>
                <Table>
                  <TableBody>
                    {Object.entries(localAssignments || {})
                      .filter(
                        ([userId, assignment]) =>
                          (assignment.ideas?.[ideaId]?.assessmentRoundIds
                            ?.length ?? 0) > 0,
                      )
                      .map(([userId, assignment]) => (
                        <TableRow key={userId}>
                          <TableCell collapsing>
                            {assignment.user.isEmailInvitee ? (
                              <BaseChip
                                title={assignment.user.email}
                                icon={MailIcon}
                                compact
                                content={`This will create a pending invitation, the user has not yet signed up to ${util.appName()}.`}
                              />
                            ) : (
                              <UserChip
                                user={assignment.user.ownerAssessor as ChipUser}
                                compact
                              />
                            )}
                          </TableCell>
                          <TableCell collapsing textAlign="right">
                            <RoundLabelsGroup>
                              {assignment.ideas?.[
                                ideaId
                              ]?.assessmentRoundIds?.map(
                                (assessmentRoundId) => {
                                  const round =
                                    assessmentRoundMap[assessmentRoundId];
                                  if (!round) return null;

                                  return (
                                    <AssessmentRoundLabel
                                      key={round.id}
                                      assessmentRound={round}
                                      onDelete={() => {
                                        const setLocalAssignments = assignment
                                          .user.isEmailInvitee
                                          ? setLocalEmailAssignments
                                          : setLocalUserAssignments;
                                        setLocalAssignments((prevState) => {
                                          const newState = {
                                            ...prevState,
                                          } as LocalAssignments;
                                          const userEntry = newState[userId];
                                          if (!userEntry) return newState;
                                          const ideaEntry =
                                            userEntry.ideas[ideaId];
                                          if (!ideaEntry) return newState;

                                          newState[userId] = {
                                            ...userEntry,
                                            ideas: {
                                              ...userEntry.ideas,
                                              [ideaId]: {
                                                ...ideaEntry,
                                                assessmentRoundIds:
                                                  ideaEntry.assessmentRoundIds.filter(
                                                    (id) => id !== round.id,
                                                  ),
                                              },
                                            },
                                          };
                                          const newRounds =
                                            newState[userId]?.ideas?.[ideaId]
                                              ?.assessmentRoundIds;
                                          if (
                                            !newRounds ||
                                            newRounds.length === 0
                                          ) {
                                            delete newState[userId]?.ideas[
                                              ideaId
                                            ];
                                          }
                                          if (
                                            Object.keys(
                                              newState[userId]?.ideas || {},
                                            ).length === 0
                                          ) {
                                            delete newState[userId];
                                          }
                                          return newState;
                                        });
                                      }}
                                    />
                                  );
                                },
                              )}
                            </RoundLabelsGroup>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </>
            ) : (
              <Message>No draft assignments.</Message>
            )}
            <H4>Active assessments</H4>
            {!util.isEmpty(inspectedIdea.assessments) ? (
              <Table>
                <TableBody>
                  {Object.values(assessmentsByUser).map((assessments) => {
                    const firstAssessment = assessments[0];
                    if (!firstAssessment) return null;
                    return (
                      <TableRow key={firstAssessment.user}>
                        <TableCell collapsing>
                          <UserChip
                            user={
                              (firstAssessment as any).ownerUser as ChipUser
                            }
                            compact
                          />
                        </TableCell>
                        <TableCell collapsing textAlign="right">
                          <RoundLabelsGroup>
                            {assessments.map((assessment) => {
                              const round =
                                assessmentRoundMap[
                                  assessment.assessmentRoundId ?? "legacy"
                                ];
                              return round ? (
                                <AssessmentRoundLabel
                                  key={round.id}
                                  assessmentRound={round}
                                  onDelete={() => {
                                    unassignAssessor(
                                      ideaId,
                                      assessment.user,
                                      round.id,
                                    );
                                  }}
                                />
                              ) : null;
                            })}
                          </RoundLabelsGroup>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <Message>No active assessments.</Message>
            )}
            {!util.isEmpty((inspectedIdea as any).emailInvites) ? (
              <>
                <H5>Invited assessors</H5>
                <Message color="blue">
                  These assessors have been invited but are currently not
                  registered. To manage their invitation, please refer to the
                  &quot;Dashboard&quot; tab.
                </Message>
                <Table>
                  {((inspectedIdea as any).emailInvites as any[]).map(
                    (emailInvite: any) => (
                      <TableRow key={emailInvite.id}>
                        <TableCell collapsing>
                          <BaseChip
                            icon={MailIcon}
                            title={emailInvite.email}
                            content={`This is a pending invitation, the user has not yet signed up to ${util.appName()}.`}
                          />
                        </TableCell>
                        <TableCell collapsing textAlign="right">
                          {Object.entries(emailInvite?.assignments || {})
                            .filter(
                              ([assignedIdeaId, _]) =>
                                assignedIdeaId === ideaId,
                            )
                            ?.map(([, assignment]: [string, any]) => {
                              return assignment.assessmentRoundIds.map(
                                (id: string) => {
                                  const round =
                                    assessmentRoundMap[id || "legacy"];
                                  return round ? (
                                    <AssessmentRoundLabel
                                      key={round.id}
                                      assessmentRound={round}
                                    />
                                  ) : null;
                                },
                              );
                            })}
                        </TableCell>
                      </TableRow>
                    ),
                  )}
                </Table>
              </>
            ) : null}
          </ModalContent>
          <ModalActions>
            <Button onClick={() => setInspectedIdea(null)}>Close</Button>
          </ModalActions>
        </>
      ) : null}
    </Modal>
  );
};
